import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaImage, FaTrophy, FaFutbol, FaCamera } from 'react-icons/fa';
import api from '../services/apiService';
import { authService } from '../services/authService';
import Header from '../Header';
import '../scss/admin/StoryManager.scss';

const StoryManager = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    thumbnail_url: '',
    button_text: '',
    button_url: '',
    expires_at: '',
    show_to_all_teams: false,
    team_id: ''
  });

  // Fetch teams and total player count on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        // Using fetch_all_teams.php instead of fetch_teams.php
        const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_all_teams.php`);
        console.log('Teams response:', response.data);
        if (response.data.success) {
          setTeams(response.data.teams);
          // Set total players count if available in the response
          if (response.data.total_players) {
            setTotalPlayers(response.data.total_players);
          } else {
            // Calculate total players by summing team lengths
            // Use parseInt to ensure string values are converted to numbers
            const totalCount = response.data.teams.reduce((sum, team) => sum + (parseInt(team.team_length, 10) || 0), 0);
            setTotalPlayers(totalCount);
          }
          // Don't auto-select any team - use "All Teams" by default
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError('Failed to load teams. Please try again later.');
      }
    };
    
    fetchTeams();
  }, []);

  // Log when selectedTeam changes
  useEffect(() => {
    console.log('Selected team changed:', selectedTeam);
  }, [selectedTeam]);

  // Fetch stories when selected team changes
  useEffect(() => {
    // Allow fetching with or without a team selection
    fetchStories();
  }, [selectedTeam]);

  const fetchStories = async () => {
    console.log('Fetching stories for team ID:', selectedTeam, 'Type:', typeof selectedTeam);
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_API_URL}/manage_stories_fixed.php?action=list`;
      
      // Only add team_id parameter if a specific team is selected
      if (selectedTeam) {
        const teamId = parseInt(selectedTeam, 10);
        if (isNaN(teamId)) {
          throw new Error('Invalid team ID');
        }
        url += `&team_id=${teamId}`;
      }
      
      console.log('Fetching stories URL:', url);
      const response = await api.get(url);
      
      console.log('Stories response:', response.data);
      console.log('Stories array:', response.data.stories);
      console.log('Stories count:', response.data.stories ? response.data.stories.length : 0);
      
      // Log detailed information about each story for debugging
      if (response.data.stories && response.data.stories.length > 0) {
        console.log('First story details:', {
          id: response.data.stories[0].story_id,
          title: response.data.stories[0].title,
          team_id: response.data.stories[0].team_id,
          team_name: response.data.stories[0].team_name,
          display_team_name: response.data.stories[0].display_team_name,
          show_to_all_teams: response.data.stories[0].show_to_all_teams
        });
      }
      
      if (response.data.success) {
        setStories(response.data.stories || []);
      } else {
        setError(response.data.message || 'Failed to load stories');
      }
    } catch (err) {
      console.error('Error fetching stories:', err);
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      setError('Failed to load stories. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (e) => {
    const teamId = e.target.value;
    console.log('Team selected from dropdown:', teamId);
    if (teamId === '') {
      setSelectedTeam(null);
    } else {
      // Ensure we're using the team ID as a number for consistency
      setSelectedTeam(parseInt(teamId, 10));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'show_to_all_teams') {
      // If toggling the "show to all teams" checkbox
      if (checked) {
        // When enabling "show to all teams", store current team selection
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          previous_team_id: prev.team_id || selectedTeam
        }));
      } else {
        // When disabling "show to all teams", restore the previous team selection
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          team_id: prev.previous_team_id || selectedTeam
        }));
      }
    } else {
      // Handle all other form inputs normally
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      thumbnail_url: '',
      button_text: '',
      button_url: '',
      expires_at: '',
      show_to_all_teams: false,
      team_id: ''
    });
    setEditingStory(null);
  };

  const handleAddClick = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditClick = (story) => {
    // Format expiry date for datetime-local input
    let formattedExpiresAt = '';
    if (story.expires_at) {
      const date = new Date(story.expires_at);
      formattedExpiresAt = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
        .toISOString()
        .slice(0, 16);
    }
    
    console.log(`Editing story: ${story.story_id}, show_to_all_teams:`, story.show_to_all_teams);
    
    // Convert show_to_all_teams to boolean (it might come as string "0"/"1" or integer 0/1)
    const showToAllTeams = story.show_to_all_teams === 1 || 
                          story.show_to_all_teams === "1" || 
                          story.show_to_all_teams === true;
    
    setFormData({
      title: story.title,
      description: story.description || '',
      image_url: story.image_url || '',
      thumbnail_url: story.thumbnail_url || '',
      button_text: story.button_text || '',
      button_url: story.button_url || '',
      expires_at: formattedExpiresAt,
      show_to_all_teams: showToAllTeams,
      team_id: showToAllTeams ? '' : story.team_id
    });
    
    setEditingStory(story.story_id);
    setShowAddModal(true);
  };

  const handleDeleteClick = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Create FormData for consistency with other API calls
      const formData = new FormData();
      formData.append('action', 'delete');
      formData.append('story_id', storyId);
      
      const response = await api.post(`${process.env.REACT_APP_API_URL}/manage_stories_fixed.php`, formData);
      
      if (response.data.success) {
        // Update local state
        setStories(stories.filter(story => String(story.story_id) !== String(storyId)));
        
        // Force refresh the stories list to ensure we have the latest data
        setTimeout(() => {
          fetchStories();
        }, 500);
      } else {
        alert(response.data.message || 'Failed to delete story');
      }
    } catch (err) {
      console.error('Error deleting story:', err);
      alert('Failed to delete story. Please try again later.');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submit triggered');
    
    try {
      const formPayload = new FormData();
      
      // Add form fields to payload
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'previous_team_id') { // Don't send our temporary tracking field
          // Convert true/false to 1/0 for show_to_all_teams
          if (key === 'show_to_all_teams') {
            value = value ? '1' : '0';
          }
          
          // Skip empty expires_at to avoid datetime errors
          if (key === 'expires_at' && !value) {
            console.log(`Skipping empty expires_at value`);
            return;
          }
          
          formPayload.append(key, value);
          console.log(`Adding field to payload: ${key} = ${value}`);
        }
      });
      
      // Add default type as 'image' since we've removed the type field
      formPayload.append('type', 'image');
      console.log('Adding default type: image');
      
      // Add action and IDs
      if (editingStory) {
        console.log(`Editing existing story: ${editingStory}`);
        formPayload.append('action', 'update');
        formPayload.append('story_id', editingStory);
      } else {
        console.log('Creating new story');
        formPayload.append('action', 'create');
        
        // Handle team_id based on show_to_all_teams setting
        if (formData.show_to_all_teams) {
          console.log('Story will be shown to all teams');
          // Let the backend find a valid team ID automatically
          // We don't need to specify a team_id, as the backend will handle this
        } else {
          // For team-specific stories, a team selection is required
          const teamId = formData.team_id || selectedTeam;
          console.log(`Team-specific story requires team selection. formData.team_id=${formData.team_id}, selectedTeam=${selectedTeam}`);
          
          if (!teamId) {
            console.error('No team selected for team-specific story');
            alert('Please select a team for this story');
            return;
          }
          
          console.log(`Using team_id: ${teamId}`);
          formPayload.append('team_id', teamId);
        }
      }
      
      // Log the final form data
      const formDataObj = {};
      for (let [key, value] of formPayload.entries()) {
        formDataObj[key] = value;
      }
      console.log('Final form data:', formDataObj);
      
      // Log the URL we're posting to
      const url = `${process.env.REACT_APP_API_URL}/manage_stories_fixed.php`;
      console.log('Posting to URL:', url);
      
      try {
        // Use axios directly with specific headers for FormData
        const response = await api.post(url, formPayload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        
        console.log('Response from server:', response.data);
        
        if (response.data.success) {
          console.log('Story saved successfully');
          
          // Close modal and reset form first for better UX
          setShowAddModal(false);
          resetForm();
          
          // Give the database a moment to update before refreshing
          setTimeout(() => {
            console.log('Refreshing stories list after successful save');
            fetchStories();
          }, 500);
        } else {
          console.error('Story save failed:', response.data.message);
          alert(response.data.message || 'Failed to save story');
        }
      } catch (axiosError) {
        console.error('Axios error:', axiosError);
        if (axiosError.response) {
          console.error('Response data:', axiosError.response.data);
          console.error('Response status:', axiosError.response.status);
          alert(`Server error: ${axiosError.response.status} - ${axiosError.response.data.message || 'Unknown error'}`);
        } else if (axiosError.request) {
          console.error('Request made but no response received:', axiosError.request);
          alert('No response received from server. Check your network connection.');
        } else {
          console.error('Error setting up request:', axiosError.message);
          alert(`Error: ${axiosError.message}`);
        }
      }
    } catch (err) {
      console.error('General error during form submission:', err);
      alert('Failed to save story. Check the console for details.');
    }
  };

  const getStoryTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <FaImage className="story-type-icon image" />;
      case 'achievement':
        return <FaTrophy className="story-type-icon achievement" />;
      case 'match':
        return <FaFutbol className="story-type-icon match" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderStories = () => {
    if (!stories || stories.length === 0) {
      return (
        <div className="no-stories-message">
          {selectedTeam ? 
            `No stories found for this team. Create a new story to get started.` : 
            `No stories found across any teams. Select a specific team or create a new story to get started.`
          }
        </div>
      );
    }
    
    return (
      <div className="stories-table">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Preview</th>
              <th>Title</th>
              <th>Team</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Views</th>
              <th>Clicks</th>
              <th>All Teams</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stories.map(story => (
              <tr key={story.story_id} className={story.is_active === 0 ? 'inactive-story' : ''}>
                <td data-label="Type">{getStoryTypeIcon(story.type)}</td>
                <td data-label="Preview" className="story-thumbnail">
                  {(story.thumbnail_url || story.image_url) && (
                    <img 
                      src={story.thumbnail_url || story.image_url} 
                      alt={story.title}
                      className="story-thumbnail-img"
                      onClick={() => window.open(story.thumbnail_url || story.image_url, '_blank')}
                    />
                  )}
                </td>
                <td className="story-title" data-label="Title">{story.title}</td>
                <td data-label="Team">{story.display_team_name || (story.show_to_all_teams === 1 ? 'All Teams' : story.team_name)}</td>
                <td data-label="Created">{formatDate(story.created_at)}</td>
                <td data-label="Expires">{formatDate(story.expires_at)}</td>
                <td data-label="Views">{story.view_count || 0}</td>
                <td data-label="Clicks">{story.click_count || 0}</td>
                <td data-label="All Teams">{parseInt(story.show_to_all_teams) === 1 ? 'Yes' : 'No'}</td>
                <td className="actions" data-label="Actions">
                  {story.type === 'image' && story.image_url && (
                    <button 
                      className="view-button"
                      onClick={() => window.open(story.image_url, '_blank')}
                      title="View Image"
                    >
                      <FaCamera />
                    </button>
                  )}
                  <button 
                    className="edit-button"
                    onClick={() => handleEditClick(story)}
                    title="Edit Story"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteClick(story.story_id)}
                    title="Delete Story"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="story-manager container">
      <Header />
      <div>
        <h1>Stories Manager</h1>
      </div>
      
      {/* Team Selection */}
      <div className="team-selector">
        <label htmlFor="team-select">Select Team:</label>
        <select 
          id="team-select" 
          value={selectedTeam ? String(selectedTeam) : ''} 
          onChange={handleTeamChange}
          disabled={loading}
        >
          <option value="">All Teams</option>
          {teams && teams.length > 0 ? (
            teams.map(team => (
              <option key={team.team_id} value={String(team.team_id)}>
                {team.team_name} ({team.team_length || 0} players)
              </option>
            ))
          ) : (
            <option value="" disabled>No teams available</option>
          )}
        </select>
        
        <button 
          className="add-story-button"
          onClick={handleAddClick}
          disabled={loading}
        >
          <FaPlus /> Add Story
        </button>
        
        {/* Debug info - remove in production */}
        <div className="debug-info">
          <p>Teams available: {teams ? teams.length : 0}</p>
          <p>Total players: {totalPlayers}</p>
          <p>Selected team ID: {selectedTeam || 'All Teams'}</p>
          <p>Stories count: {stories ? stories.length : 0}</p>
          {selectedTeam && teams && (
            <p>Players in team: {teams.find(t => t.team_id == selectedTeam)?.team_length || 'unknown'}</p>
          )}
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {/* Loading Indicator */}
      {loading && (
        <div className="loading-indicator">
          Loading stories...
        </div>
      )}
      
      {/* Stories List */}
      {!loading && !error && (
        <>
          {renderStories()}
        </>
      )}
      
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="story-modal">
            <h2>{editingStory ? 'Edit Story' : 'Add New Story'}</h2>
            
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title:</label>
                <input 
                  type="text" 
                  id="title" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleInputChange}
                  required
                  maxLength="255"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description:</label>
                <textarea 
                  id="description" 
                  name="description" 
                  value={formData.description} 
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="image_url">Image URL:</label>
                <input 
                  type="text" 
                  id="image_url" 
                  name="image_url" 
                  value={formData.image_url} 
                  onChange={handleInputChange}
                  required
                  maxLength="255"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="thumbnail_url">Thumbnail URL (optional):</label>
                <input 
                  type="text" 
                  id="thumbnail_url" 
                  name="thumbnail_url" 
                  value={formData.thumbnail_url} 
                  onChange={handleInputChange}
                  maxLength="255"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="button_text">Button Text (optional):</label>
                <input 
                  type="text" 
                  id="button_text" 
                  name="button_text" 
                  value={formData.button_text} 
                  onChange={handleInputChange}
                  maxLength="50"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="button_url">Button URL (optional):</label>
                <input 
                  type="url" 
                  id="button_url" 
                  name="button_url" 
                  value={formData.button_url} 
                  onChange={handleInputChange}
                  maxLength="255"
                />
              </div>
              
              <div className="form-group checkbox-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    name="show_to_all_teams"
                    checked={formData.show_to_all_teams || false}
                    onChange={handleInputChange}
                    id="show_to_all_teams"
                  />
                  <label htmlFor="show_to_all_teams" className="checkbox-label">
                    Show to all teams
                  </label>
                </div>
              </div>
              
              <div className="form-group team-selection">
                <label>Team</label>
                <select
                  className={`form-control ${formData.show_to_all_teams ? 'disabled-select' : ''}`}
                  name="team_id"
                  value={formData.team_id || selectedTeam || ''}
                  onChange={handleInputChange}
                  disabled={formData.show_to_all_teams}
                  required={!formData.show_to_all_teams}
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.team_id} value={team.team_id}>
                      {team.team_name}
                    </option>
                  ))}
                </select>
                {formData.show_to_all_teams && (
                  <div className="all-teams-message">
                    This story will be shown to players in all teams
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="expires_at">Expires At (optional):</label>
                <input 
                  type="datetime-local" 
                  id="expires_at" 
                  name="expires_at" 
                  value={formData.expires_at} 
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {editingStory ? 'Update Story' : 'Create Story'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryManager; 